/**
 * AMP PDF Processor - Browser Application
 *
 * Handles file uploads, PDF extraction, and result display.
 * All processing happens client-side - no server required.
 *
 * A single statement PDF may contain several accounts; results are grouped by
 * account, each with its own summary, per-contract economics, P&L blocks,
 * fee breakdown and trades table.
 */

import { extractFromFile } from './extractor';
import { getUnmappedDescriptions, clearUnmappedDescriptions } from './symbol-resolver';
import { downloadFullResultXLSX } from './xlsx-export';
import type { ProcessingResult, AccountStatement, Trade } from './types';

// API endpoint for Send to Dev feature
export const DEV_CONFIG = {
  apiEndpoint: 'https://jqlf68lak1.execute-api.us-east-1.amazonaws.com/prod/submit',
  enabled: true,
};

type SortState = { column: string | null; dir: 'asc' | 'desc' };

export class PDFProcessor {
  private panel: HTMLElement;
  private dropzone: HTMLElement;
  private fileInput: HTMLInputElement;
  private processing: HTMLElement;
  private processingText: HTMLElement;
  private summary: HTMLElement;
  private resultsContainer: HTMLElement;
  private errorState: HTMLElement;
  private downloadButtons: HTMLElement;

  private processedData: ProcessingResult | null = null;
  private currentFilename = '';
  private sortStates: SortState[] = [];

  constructor(panelId: string) {
    this.panel = document.getElementById(panelId)!;
    this.dropzone = this.panel.querySelector('.dropzone')!;
    this.fileInput = this.panel.querySelector('input[type="file"]')!;
    this.processing = this.panel.querySelector('.processing')!;
    this.processingText = this.panel.querySelector('.processing-text')!;
    this.summary = this.panel.querySelector('.summary')!;
    this.resultsContainer = this.panel.querySelector('.results-container')!;
    this.errorState = this.panel.querySelector('.error-state')!;
    this.downloadButtons = this.panel.querySelector('.download-buttons')!;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.dropzone.addEventListener('click', () => this.fileInput.click());

    this.fileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) this.handleFile(target.files[0]);
    });

    this.dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzone.classList.add('dragover');
    });
    this.dropzone.addEventListener('dragleave', () => this.dropzone.classList.remove('dragover'));
    this.dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzone.classList.remove('dragover');
      if (e.dataTransfer?.files.length) this.handleFile(e.dataTransfer.files[0]);
    });

    this.panel.querySelector('#download-json')?.addEventListener('click', () => this.downloadJSON());
    this.panel.querySelector('#download-xlsx')?.addEventListener('click', () => this.downloadXLSX());
    this.panel.querySelector('#send-to-dev')?.addEventListener('click', () => this.sendToDev());
  }

  private async handleFile(file: File): Promise<void> {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.showError('Invalid file type', 'Please upload a PDF file.');
      return;
    }

    this.currentFilename = file.name.replace(/\.[pP][dD][fF]$/, '');
    this.resetUI();
    this.showProcessing('Processing PDF...');

    try {
      clearUnmappedDescriptions();
      const result = await extractFromFile(file);
      if (!result.success) throw new Error(result.error || 'Failed to extract data from PDF');

      this.processedData = {
        success: true,
        method: 'pdfjs',
        sourceFile: file.name,
        processedAt: new Date().toISOString(),
        accounts: result.accounts,
        unmappedContracts: getUnmappedDescriptions(),
      };
      this.sortStates = result.accounts.map(() => ({ column: null, dir: 'asc' }));

      this.displayResults(this.processedData);
      this.showDownloadButtons();
    } catch (error) {
      console.error('Processing error:', error);
      this.showError('Processing failed', error instanceof Error ? error.message : String(error));
    }
  }

  private displayResults(data: ProcessingResult): void {
    this.hideProcessing();

    const accounts = data.accounts;
    const totalTrades = accounts.reduce((s, a) => s + a.trades.length, 0);
    const totalRealized = accounts.reduce((s, a) => s + a.reconciliation.psBlockPnLSum, 0);
    const totalCommission = accounts.reduce((s, a) => s + a.fees.total, 0);
    const totalNet = totalRealized - Math.abs(totalCommission);

    this.summary.classList.add('active');
    const cards = this.summary.querySelectorAll('.summary-card');
    cards[0].querySelector('.summary-value')!.textContent = String(accounts.length);
    cards[1].querySelector('.summary-value')!.textContent = String(totalTrades);
    const realizedEl = cards[2].querySelector('.summary-value')!;
    realizedEl.textContent = money(totalRealized);
    cards[2].classList.toggle('buy', totalRealized >= 0);
    cards[2].classList.toggle('sell', totalRealized < 0);
    cards[3].querySelector('.summary-value')!.textContent = money(-Math.abs(totalCommission));
    const netEl = cards[4].querySelector('.summary-value')!;
    netEl.textContent = money(totalNet);
    cards[4].classList.toggle('buy', totalNet >= 0);
    cards[4].classList.toggle('sell', totalNet < 0);

    this.resultsContainer.style.display = 'flex';
    const header = this.resultsContainer.querySelector('.results-header')!;
    header.querySelector('.results-count')!.textContent =
      `${accounts.length} account${accounts.length !== 1 ? 's' : ''}, ${totalTrades} trades`;
    header.querySelector('.results-method')!.textContent = data.method;

    const wrapper = this.resultsContainer.querySelector('.table-wrapper')! as HTMLElement;
    wrapper.innerHTML = accounts.map((a, i) => this.renderAccount(a, i)).join('');
    this.attachSortHandlers(accounts, wrapper);
  }

  private renderAccount(a: AccountStatement, idx: number): string {
    const rec = a.reconciliation;
    const recBadge = (ok: boolean | null) =>
      ok == null ? '' : ok
        ? '<span class="recon ok">reconciled</span>'
        : '<span class="recon bad">mismatch</span>';

    const cf = a.cashFlow;
    const depositStat = cf.deposits
      ? `<div><span>Deposits</span><strong class="pos">${money(cf.deposits)}</strong></div>` : '';
    const withdrawalStat = cf.withdrawals
      ? `<div><span>Withdrawals</span><strong class="neg">${money(cf.withdrawals)}</strong></div>` : '';
    const dataFeeStat = cf.dataFees
      ? `<div><span>Data Fees</span><strong class="neg">${money(cf.dataFees)}</strong></div>` : '';
    const netPnL = rec.psBlockPnLSum - Math.abs(a.fees.total);
    const stats = `
      <div class="acct-stats">
        <div><span>Realized P&amp;L</span><strong class="${rec.psBlockPnLSum >= 0 ? 'pos' : 'neg'}">${money(rec.psBlockPnLSum)}</strong>${recBadge(rec.pnlMatches)}</div>
        <div><span>Commission</span><strong class="neg">${money(-Math.abs(a.fees.total))}</strong>${recBadge(rec.feesMatch)}</div>
        <div><span>Net P&amp;L</span><strong class="${netPnL >= 0 ? 'pos' : 'neg'}">${money(netPnL)}</strong></div>
        ${dataFeeStat}
        <div><span>Net Liq. Value</span><strong>${money(a.summary?.netLiquidatingValue ?? null)}</strong></div>
        ${depositStat}
        ${withdrawalStat}
        <div><span>Trades</span><strong>${a.trades.length}</strong></div>
        <div><span>Buy / Sell</span><strong>${a.buyQty} / ${a.sellQty}</strong></div>
      </div>`;

    const econRows = a.economics.map(e => `
      <tr>
        <td>${e.contractType}</td>
        <td>${num(e.pointValue)}</td>
        <td>${num(e.tickSize)}</td>
        <td>${num(e.tickValue)}</td>
        <td>${num(e.feePerContract)}</td>
        <td>${e.totalContracts}</td>
        <td class="${e.totalRealizedPnL >= 0 ? 'pos' : 'neg'}">${money(e.totalRealizedPnL)}</td>
        <td>${e.sampleBlocks}</td>
      </tr>`).join('');
    const econTable = a.economics.length ? `
      <details open class="acct-section">
        <summary>Per-contract economics</summary>
        <table class="mini">
          <thead><tr>
            <th>Contract</th><th>$/Point</th><th>Tick Size</th><th>Tick Value</th>
            <th>Fee/Ctr</th><th>Contracts</th><th>Realized P&amp;L</th><th>Blocks</th>
          </tr></thead>
          <tbody>${econRows}</tbody>
        </table>
      </details>` : '';

    const posRows = a.positions.map(p => `
      <tr>
        <td>${escapeHtml(p.contractType)}</td>
        <td class="direction-${p.direction === 'LONG' ? 'buy' : 'sell'}">${p.direction}</td>
        <td>${p.quantity}</td>
        <td>${escapeHtml(p.entryId)}</td>
        <td>${num(p.entryPrice)}</td>
        <td>${escapeHtml(p.exitId)}</td>
        <td>${num(p.exitPrice)}</td>
        <td>${num(p.points)}</td>
        <td class="${(p.grossPnL ?? 0) >= 0 ? 'pos' : 'neg'}">${p.grossPnL == null ? '-' : money(p.grossPnL)}</td>
        <td class="neg">${money(-p.commission)}</td>
        <td class="${(p.netPnL ?? 0) >= 0 ? 'pos' : 'neg'}">${p.netPnL == null ? '-' : money(p.netPnL)}</td>
      </tr>`).join('');
    const posTotal = a.positions.reduce((s, p) => s + (p.netPnL ?? 0), 0);
    const positionsTable = a.positions.length ? `
      <details open class="acct-section">
        <summary>Closed positions — FIFO (${a.positions.length}) · net ${money(posTotal)}</summary>
        <div class="trades-host">
          <table class="mini">
            <thead><tr>
              <th>Contract</th><th>Side</th><th>Qty</th><th>Entry Id</th><th>Entry</th>
              <th>Exit Id</th><th>Exit</th><th>Points</th><th>Gross P&amp;L</th><th>Comm</th><th>Net P&amp;L</th>
            </tr></thead>
            <tbody>${posRows}</tbody>
          </table>
        </div>
      </details>` : '';

    const openRows = a.openPositions.map(p => `
      <tr>
        <td>${escapeHtml(p.contractType)}</td>
        <td>${escapeHtml(p.expiration || p.contractMonth)}</td>
        <td class="direction-${p.direction === 'LONG' ? 'buy' : 'sell'}">${p.direction}</td>
        <td>${p.quantity}</td>
        <td>${num(p.avgPrice)}</td>
      </tr>`).join('');
    const openTable = a.openPositions.length ? `
      <details open class="acct-section">
        <summary>Open positions (${a.openPositions.length}) — unrealized; see OPEN TRADE EQUITY ${money(a.summary?.openTradeEquity ?? null)}</summary>
        <div class="trades-host">
          <table class="mini">
            <thead><tr><th>Contract</th><th>Expiration</th><th>Side</th><th>Qty</th><th>Avg Price</th></tr></thead>
            <tbody>${openRows}</tbody>
          </table>
        </div>
      </details>` : '';

    const journalRows = a.journal.map(j => `
      <tr>
        <td>${escapeHtml(j.date)}</td>
        <td><span class="cat cat-${j.category.toLowerCase()}">${j.category.replace('_', ' ')}</span></td>
        <td>${escapeHtml(j.description)}</td>
        <td class="${j.drcr === 'CR' ? 'pos' : 'neg'}">${money(j.drcr === 'CR' ? j.amount : -j.amount)}</td>
      </tr>`).join('');
    const journalTable = a.journal.length ? `
      <details class="acct-section">
        <summary>Cash movements (${a.journal.length})</summary>
        <div class="trades-host">
          <table class="mini">
            <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th></tr></thead>
            <tbody>${journalRows}</tbody>
          </table>
        </div>
      </details>` : '';

    const tradesTable = `
      <details class="acct-section">
        <summary>Trades (${a.trades.length})</summary>
        <div class="trades-host" data-acct="${idx}">${this.renderTrades(a.trades, idx)}</div>
      </details>`;

    return `
      <section class="account-block">
        <div class="account-head">
          <h3>Account ${escapeHtml(a.accountNumber)}</h3>
          <span class="period">${a.period.from || ''}${a.period.to ? ` – ${a.period.to}` : ''}</span>
        </div>
        ${stats}
        ${econTable}
        ${positionsTable}
        ${openTable}
        ${journalTable}
        ${tradesTable}
      </section>`;
  }

  private renderTrades(trades: Trade[], idx: number): string {
    if (trades.length === 0) return '<div class="empty-state"><div class="empty-state-icon">No trades</div></div>';
    const columns = ['Date', 'Trade_Number', 'Market', 'Direction', 'Quantity',
      'Contract_Type', 'Contract_Month', 'Expiration', 'Trade_Price', 'Commission', 'Currency', 'Original_Description'];

    const sort = this.sortStates[idx];
    let rows = trades;
    if (sort?.column) {
      const col = sort.column;
      rows = [...trades].sort((a, b) => {
        let av: string | number = (a as any)[col] ?? '';
        let bv: string | number = (b as any)[col] ?? '';
        if (col === 'Quantity' || col === 'Commission' || col === 'Trade_Price') { av = Number(av); bv = Number(bv); }
        if (av < bv) return sort.dir === 'asc' ? -1 : 1;
        if (av > bv) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    let html = '<table><thead><tr>';
    columns.forEach(col => {
      const cls = sort?.column === col ? (sort.dir === 'asc' ? 'sorted-asc' : 'sorted-desc') : '';
      html += `<th class="${cls}" data-column="${col}" data-acct="${idx}">${col.replace(/_/g, ' ')}</th>`;
    });
    html += '</tr></thead><tbody>';
    rows.forEach(t => {
      html += '<tr>';
      columns.forEach(col => {
        const v = (t as any)[col] ?? '';
        const cls = col === 'Direction' ? `direction-${String(v).toLowerCase()}` : '';
        html += `<td class="${cls}">${escapeHtml(String(v))}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    return html;
  }

  private attachSortHandlers(accounts: AccountStatement[], wrapper: HTMLElement): void {
    wrapper.addEventListener('click', (e) => {
      const th = (e.target as HTMLElement).closest('th[data-column]') as HTMLElement | null;
      if (!th) return;
      const idx = Number(th.getAttribute('data-acct'));
      const col = th.getAttribute('data-column')!;
      const st = this.sortStates[idx];
      if (st.column === col) st.dir = st.dir === 'asc' ? 'desc' : 'asc';
      else { st.column = col; st.dir = 'asc'; }
      const host = wrapper.querySelector(`.trades-host[data-acct="${idx}"]`);
      if (host) host.innerHTML = this.renderTrades(accounts[idx].trades, idx);
    });
  }

  private showDownloadButtons(): void { this.downloadButtons.style.display = 'flex'; }
  private hideDownloadButtons(): void { this.downloadButtons.style.display = 'none'; }

  private downloadJSON(): void {
    if (!this.processedData) return;
    const json = JSON.stringify(this.processedData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.currentFilename}_statement.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private downloadXLSX(): void {
    if (!this.processedData) return;
    downloadFullResultXLSX(this.processedData, this.currentFilename);
  }

  private allTrades(): Trade[] {
    return this.processedData ? this.processedData.accounts.flatMap(a => a.trades) : [];
  }

  private showSendModal(): void {
    if (!this.processedData) return;
    const modal = document.getElementById('send-modal');
    const summary = document.getElementById('modal-summary');
    if (!modal || !summary) return;

    const trades = this.allTrades();
    const totalTrades = trades.length;
    const unknownTrades = trades.filter(t => t.Contract_Type === 'UNKNOWN').length;

    const renderSummary = (all: boolean) => {
      summary.innerHTML = all
        ? `<strong>What will be sent:</strong><br>All <strong>${totalTrades}</strong> trades (includes ${unknownTrades} unknown)`
        : `<strong>What will be sent:</strong><br>Only <strong>${unknownTrades}</strong> unknown trade${unknownTrades !== 1 ? 's' : ''}`;
    };
    renderSummary(false);
    modal.classList.add('active');

    const cancelBtn = document.getElementById('modal-cancel');
    const sendBtn = document.getElementById('modal-send');
    const radios = modal.querySelectorAll('input[name="send-option"]');
    const onChange = () => {
      const sel = modal.querySelector('input[name="send-option"]:checked') as HTMLInputElement;
      renderSummary(sel?.value === 'all');
    };
    const cleanup = () => {
      cancelBtn?.removeEventListener('click', onCancel);
      sendBtn?.removeEventListener('click', onSend);
      radios.forEach(r => r.removeEventListener('change', onChange));
    };
    const onCancel = () => { modal.classList.remove('active'); cleanup(); };
    const onSend = () => {
      const sel = modal.querySelector('input[name="send-option"]:checked') as HTMLInputElement;
      const all = sel?.value === 'all';
      modal.classList.remove('active');
      cleanup();
      this.executeSendToDev(all);
    };
    radios.forEach(r => r.addEventListener('change', onChange));
    cancelBtn?.addEventListener('click', onCancel);
    sendBtn?.addEventListener('click', onSend);
    modal.addEventListener('click', (e) => { if (e.target === modal) onCancel(); }, { once: true });
  }

  private async executeSendToDev(sendAll: boolean): Promise<void> {
    if (!this.processedData) return;
    if (!DEV_CONFIG.enabled || !DEV_CONFIG.apiEndpoint) { alert('Send to Dev is not yet configured.'); return; }

    try {
      const all = this.allTrades();
      // Strip account number and execution/trade id before sending — neither is
      // needed to improve symbol detection, and both are account-specific.
      const anonymize = (t: Trade) => ({ ...t, Account: '', Trade_Number: '' });
      const tradesToSend = (sendAll ? all : all.filter(t => t.Contract_Type === 'UNKNOWN')).map(anonymize);

      const response = await fetch(DEV_CONFIG.apiEndpoint, {
        method: 'POST',
        body: JSON.stringify({ timestamp: new Date().toISOString(), trades: tradesToSend }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        alert(`Thank you! ${tradesToSend.length} trade${tradesToSend.length !== 1 ? 's' : ''} sent successfully.`);
      } else {
        throw new Error(`Upload failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Send to Dev error:', error);
      alert('Failed to send data. Please try again later.');
    }
  }

  private sendToDev(): void { this.showSendModal(); }

  public clearCache(): void {
    this.processedData = null;
    this.currentFilename = '';
    localStorage.removeItem('amp-pdf-processor-cache');
    this.fileInput.value = '';
    this.resetUI();
    clearUnmappedDescriptions();
    console.log('Cache cleared');
  }

  private showProcessing(message: string): void {
    this.processing.classList.add('active');
    this.processingText.textContent = message;
  }
  private hideProcessing(): void { this.processing.classList.remove('active'); }

  private showError(title: string, message: string): void {
    this.hideProcessing();
    this.errorState.classList.add('active');
    this.errorState.querySelector('.error-title')!.textContent = title;
    this.errorState.querySelector('.error-message')!.textContent = message;
  }

  private resetUI(): void {
    this.summary.classList.remove('active');
    this.resultsContainer.style.display = 'none';
    this.errorState.classList.remove('active');
    this.hideProcessing();
    this.hideDownloadButtons();
    this.processedData = null;
  }
}

// --- formatting helpers ---
function money(v: number | null): string {
  if (v == null) return '-';
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function num(v: number | null): string { return v == null ? '-' : String(v); }
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
